import { Link } from "react-router-dom";
import { Sparkles, Plus, Phone } from "lucide-react";
import { useSeo } from "@/hooks/useSeo";
import { money } from "@/lib/api";
import { PageHero, SectionHeader, CtaBand, IncludesList, BookNowButton, useCatalogParts } from "@/components/marketing/Sections";
import { SERVICE_ICONS, CallOnlyCard, TrustCounter, useServiceGroups } from "@/components/marketing/ServiceCards";
import { useSettings } from "@/context/SettingsContext";

const IMAGES = { standard: "https://images.unsplash.com/photo-1649083048337-4aeb6dda80bb?crop=entropy&cs=srgb&fm=jpg&q=80&w=900", deep: "https://images.unsplash.com/photo-1556912173-3bb406ef7e77?crop=entropy&cs=srgb&fm=jpg&q=80&w=900", special: "https://images.unsplash.com/photo-1556911220-bff31c812dba?crop=entropy&cs=srgb&fm=jpg&q=80&w=900" };

function SpecialRates() {
  const { settings: s } = useSettings();
  if (!s) return null;
  const th = s.special_washroom_threshold ?? 2;
  return (
    <div className="mt-5 grid sm:grid-cols-3 gap-3" data-testid="special-rates">
      {s.special_kitchen_enabled !== false && <div className="rounded-2xl bg-[#F3F1EC] p-4"><p className="text-xs uppercase tracking-wider text-[#5A606A]">Kitchen Only</p><p className="font-display text-2xl text-[var(--kc-blue)]" data-testid="special-rate-kitchen">{money(s.special_kitchen_price ?? 100)}</p><p className="text-xs text-[#5A606A]">Flat rate</p></div>}
      {s.special_washrooms_enabled !== false && <>
        <div className="rounded-2xl bg-[#F3F1EC] p-4"><p className="text-xs uppercase tracking-wider text-[#5A606A]">Up to {th} washrooms</p><p className="font-display text-2xl text-[var(--kc-blue)]" data-testid="special-rate-washrooms-base">{money(s.special_washrooms_base_price ?? 100)}</p><p className="text-xs text-[#5A606A]">Flat rate</p></div>
        <div className="rounded-2xl bg-[#F3F1EC] p-4"><p className="text-xs uppercase tracking-wider text-[#5A606A]">{th + 1}+ washrooms</p><p className="font-display text-2xl text-[var(--kc-blue)]" data-testid="special-rate-washrooms-above">{money(s.special_washrooms_above_price ?? 130)}</p><p className="text-xs text-[#5A606A]">Flat rate</p></div>
      </>}
    </div>
  );
}

export default function Services() {
  const { catalog, addOns, rules } = useCatalogParts();
  const { online, callOnly } = useServiceGroups();
  useSeo("Residential Cleaning Services", "Regular, deep and special flat-rate cleaning you can book online, plus Airbnb, office, post-construction and move-in/move-out cleaning by custom quote across the GTA.");
  const startingFor = (code) => { const list = (catalog?.packages || []).filter((p) => p.service_code === code && p.starting_price); return list.length ? Math.min(...list.map((p) => p.starting_price)) : null; };

  return (
    <div>
      <PageHero eyebrow="Our services" title="Cleaning that fits your home and your schedule" intro="Book Regular, Deep or flat-rate Special Cleaning online in minutes — or call us for a custom quote on specialty jobs.">
        <div className="flex flex-col sm:flex-row gap-4 sm:items-center"><BookNowButton source="services-hero" label="BOOK A CLEANING" testId="services-hero-book-now" /><TrustCounter /></div>
      </PageHero>

      <section className="kc-section">
        <div className="kc-container">
          <SectionHeader eyebrow="Book online" title="Instant price · Book in 4 steps" intro="Priced upfront by our live rate card. Guests welcome — members save extra." />
          <div className="mt-12 space-y-16" data-testid="services-list">
            {online.map((s, i) => {
              const Icon = SERVICE_ICONS[s.icon] || Sparkles; const from = s.pricing_mode === "special" ? null : startingFor(s.code);
              return (
                <article key={s.code} className={`grid lg:grid-cols-12 gap-10 items-center ${i % 2 ? "lg:[&>*:first-child]:order-2" : ""}`} data-testid={`service-card-${s.code}`}>
                  <div className="lg:col-span-6"><img src={IMAGES[s.code] || IMAGES.standard} alt={`${s.name} in progress`} loading="lazy" className="rounded-3xl w-full aspect-[4/3] object-cover shadow-xl" /></div>
                  <div className="lg:col-span-6">
                    <div className="flex items-center gap-3"><span className="h-11 w-11 rounded-full bg-[#F3F1EC] grid place-items-center"><Icon className="w-5 h-5 text-[var(--kc-blue)]" /></span><span className="rounded-full bg-[#ECFDF3] text-[#067647] px-3 h-7 inline-flex items-center text-xs font-semibold">BOOK ONLINE</span></div>
                    <h2 className="mt-5 text-2xl sm:text-3xl font-semibold">{s.name}</h2>
                    <p className="mt-3 text-[#5A606A] text-base sm:text-lg leading-relaxed">{s.short_description}</p>
                    <IncludesList items={s.includes} />
                    {s.pricing_mode === "special" && <SpecialRates />}
                    <div className="mt-6 flex flex-wrap items-center gap-4">
                      {from && <p className="text-sm text-[#5A606A]">Starting from <strong className="text-[var(--kc-blue)] text-2xl font-display" data-testid={`service-${s.code}-from`}>{money(from)}</strong></p>}
                      <BookNowButton source={`services-${s.code}`} label="BOOK NOW" testId={`service-${s.code}-book`} />
                      {s.pricing_mode !== "special" && <Link to="/pricing" className="btn-ghost text-[var(--kc-blue)]" data-testid={`service-${s.code}-pricing-link`}>See pricing</Link>}
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {callOnly.length > 0 && (
        <section className="kc-section bg-white border-y border-[#E5E2DA]" data-testid="services-call-only">
          <div className="kc-container">
            <SectionHeader eyebrow="Custom quote · Call to book" title="Specialty cleaning priced by quote" intro="Every space is different — tell us about yours and we'll quote it properly. No online pricing for these services." />
            <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">{callOnly.map((s) => <CallOnlyCard key={s.code} s={s} />)}</div>
            <p className="mt-6 text-xs text-[#5A606A] inline-flex items-center gap-1.5"><Phone className="w-3.5 h-3.5" /> On mobile, tap CALL FOR QUOTE to dial us directly.</p>
          </div>
        </section>
      )}

      {addOns.length > 0 && (
        <section className="kc-section">
          <div className="kc-container">
            <SectionHeader eyebrow="Add-ons" title="Extra touches" intro="Add to any online booking during checkout." />
            <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="services-addons">
              {addOns.map((a) => (
                <div key={a.code} className="kc-card p-6 flex items-start gap-4" data-testid={`addon-card-${a.code}`}>
                  <span className="h-10 w-10 rounded-full bg-[#F3F1EC] grid place-items-center shrink-0"><Plus className="w-5 h-5 text-[var(--kc-terracotta)]" /></span>
                  <div><h3 className="font-semibold">{a.name}</h3><p className="text-sm text-[#5A606A] mt-1">{a.description}</p><p className="mt-2 font-display text-xl text-[var(--kc-blue)]">+{money(a.price)}</p></div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {rules.SUPPLIES_DISCOUNT?.enabled && (
        <section className="kc-container pb-8"><p className="text-sm text-[#5A606A]">Prefer to supply your own products? Save {rules.SUPPLIES_DISCOUNT.value}% on Regular and Deep cleaning when you provide cleaning supplies — just choose that option when booking.</p></section>
      )}
      <CtaBand source="services" />
    </div>
  );
}
